import { IconButton, Tooltip } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useMaintenanceTaskElementSelectorDialog } from "routes/component-library/reference-selector/use-maintenance-task-element-selector-dialog"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { confirm } from "lib/@dialogs/confirm"
import Iconify from "minimals-template/components/Iconify"
import { preventPropagation } from "lib/prevent"
import noop from "lib/noop"
import { isTaskComponentLinked } from "./component-ref-link-state"

const ICON_SIZE = { width: 18, height: 18 }

/** Unlinked: muted puzzle outline */
const unlinkSx = {
    p: 0.25,
    mr: 0.5,
    color: "primary.main",
    width: 24,
    height: 24,
}

/** Linked: solid primary background */
const linkedSx = {
    p: 0.25,
    mr: 0.5,
    bgcolor: "primary.main",
    color: "primary.contrastText",
    "&:hover": { bgcolor: "primary.dark" },
    width: 24,
    height: 24,
}

/**
 * Row-level component link indicator in TaskRowStart.
 *
 * Always visible as a column icon:
 * - No componentRef: muted puzzle outline — click opens component selector
 * - componentRef set: solid puzzle icon with component name tooltip — click confirms then clears link
 */
export function ComponentRefRowLink() {
    const { componentRef, linkedFields, onSetComponentRef, onClearComponentRef, editMode, task, componentTask } =
        useBoundContext()
    const openElementSelector = useMaintenanceTaskElementSelectorDialog({
        title: "Link Component Task",
        applyCaption: "Link",
        taskOnly: true,
    })
    const isLinked = isTaskComponentLinked(componentRef, linkedFields)

    if (!editMode || task?.isHeader) return null

    if (!isLinked) {
        return (
            <Tooltip title="Link a component task">
                <IconButton
                    size="small"
                    onClick={preventPropagation(handleLink)}
                    onMouseDown={preventPropagation(noop)}
                    sx={unlinkSx}
                    aria-label="Link component task"
                    data-cy="link-component-task"
                >
                    <Iconify icon="mdi:puzzle-outline" sx={ICON_SIZE} />
                </IconButton>
            </Tooltip>
        )
    }

    const tooltipLabel = buildTooltip()

    return (
        <Tooltip title={tooltipLabel}>
            <IconButton
                size="small"
                onClick={preventPropagation(handleUnlink)}
                onMouseDown={preventPropagation(noop)}
                sx={linkedSx}
                aria-label="Unlink component"
            >
                <Iconify icon="mdi:puzzle" sx={ICON_SIZE} />
            </IconButton>
        </Tooltip>
    )

    function buildTooltip() {
        if (!componentTask || componentTask === IS_LOADING) return "Component linked"
        const path = formatCategoryPath(componentTask.categoryPath)
        const { heading } = componentTask
        if (path && heading) return `${path} › ${heading}`
        return heading ?? componentRef
    }

    async function handleLink() {
        const reference = await openElementSelector()
        if (!reference?.id) return
        onSetComponentRef?.(reference.id)
    }

    async function handleUnlink() {
        const label = buildTooltip()
        const ok = await confirm(`Remove link to "${label}"?`, "Unlink component")
        if (!ok) return
        onClearComponentRef?.()
    }
}

function formatCategoryPath(path) {
    if (!path) return ""
    return path.replace(/^Components\//, "").replace(/\//g, " › ")
}
